import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiX, FiPlus } from 'react-icons/fi';
import api, { congregacaoService } from '../../services/api';
import MembroBuscaSelect from '../../components/MembroBuscaSelect';
import './CadastroColaborador.css';

const EditarColaborador = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [cargos, setCargos] = useState([]);
  const [congregacoes, setCongregacoes] = useState([]);
  const [loadingCargos, setLoadingCargos] = useState(true);
  const [loadingCongregacoes, setLoadingCongregacoes] = useState(true);
  const [cargoSelecionado, setCargoSelecionado] = useState('');
  const [formData, setFormData] = useState({
    nome_completo: '',
    email: '',
    tipo_pessoa: 'irmao',
    cargo_ids: [],
    telefone: '',
    congregacao_id: '',
    irmandade_id: ''
  });
  const [membrosJovensAdultos, setMembrosJovensAdultos] = useState([]);
  const [loadingIrmandade, setLoadingIrmandade] = useState(true);

  const fetchColaborador = useCallback(async () => {
    try {
      const response = await api.get(`/colaboradores/${id}`);
      const colaborador = response.data;
      
      // Converter cargo_ids string para array se necessário
      let cargoIds = [];
      if (colaborador.cargo_ids) {
        cargoIds = typeof colaborador.cargo_ids === 'string' 
          ? colaborador.cargo_ids.split(',').map(id => parseInt(id.trim()))
          : colaborador.cargo_ids;
      }
      
      const irmandadeId = colaborador.irmandade_id ?? colaborador.irmandadeId;
      setFormData({
        nome_completo: colaborador.nome_completo || colaborador.nomeCompleto || '',
        email: colaborador.email || '',
        tipo_pessoa: colaborador.tipo_pessoa || colaborador.tipoPessoa || 'irmao',
        cargo_ids: cargoIds,
        telefone: colaborador.telefone || '',
        congregacao_id: colaborador.congregacao_id ?? colaborador.congregacaoId ?? '',
        irmandade_id: irmandadeId !== undefined && irmandadeId !== null ? String(irmandadeId) : ''
      });
    } catch (error) {
      console.error('Erro ao buscar colaborador:', error);
      navigate('/colaboradores');
    } finally {
      setLoadingData(false);
    }
  }, [id, navigate]);

  // Carregar cargos disponíveis
  const carregarCargos = useCallback(async () => {
    try {
      const response = await api.get('/cargos');
      setCargos(response.data);
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
    } finally {
      setLoadingCargos(false);
    }
  }, []);

  // Carregar congregações disponíveis
  const carregarCongregacoes = useCallback(async () => {
    try {
      const response = await congregacaoService.getAtivas();
      setCongregacoes(response.data);
    } catch (error) {
      console.error('Erro ao carregar congregações:', error);
    } finally {
      setLoadingCongregacoes(false);
    }
  }, []);

  // Carregar membros jovens e adultos para vínculo
  const carregarMembrosJovensAdultos = useCallback(async () => {
    try {
      const [resJovens, resAdultos] = await Promise.all([
        api.get('/irmandade/todos?categoria=jovem'),
        api.get('/irmandade/todos?categoria=adulto')
      ]);
      const jovens = Array.isArray(resJovens.data) ? resJovens.data : [];
      const adultos = Array.isArray(resAdultos.data) ? resAdultos.data : [];
      const lista = [...jovens, ...adultos].sort((a, b) =>
        (a.nome_completo || a.nomeCompleto || '').localeCompare(b.nome_completo || b.nomeCompleto || '')
      );
      setMembrosJovensAdultos(lista);
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
    } finally {
      setLoadingIrmandade(false);
    }
  }, []);

  useEffect(() => {
    fetchColaborador();
    carregarCargos();
    carregarCongregacoes();
    carregarMembrosJovensAdultos();
  }, [fetchColaborador, carregarCargos, carregarCongregacoes, carregarMembrosJovensAdultos]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'irmandade_id') {
      const membro = membrosJovensAdultos.find(m => String(m.id) === String(value));
      setFormData(prev => ({
        ...prev,
        irmandade_id: value,
        ...(membro ? {
          nome_completo: membro.nome_completo ?? membro.nomeCompleto ?? prev.nome_completo,
          email: membro.email ?? prev.email,
          telefone: membro.telefone ?? prev.telefone
        } : {})
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const adicionarCargo = () => {
    if (cargoSelecionado && !formData.cargo_ids.includes(parseInt(cargoSelecionado))) {
      setFormData(prev => ({
        ...prev,
        cargo_ids: [...prev.cargo_ids, parseInt(cargoSelecionado)]
      }));
      setCargoSelecionado('');
    }
  };

  // Função para adicionar cargo automaticamente quando selecionado
  const handleCargoChange = (e) => {
    const cargoId = e.target.value;
    if (cargoId && !formData.cargo_ids.includes(parseInt(cargoId))) {
      setFormData(prev => ({
        ...prev,
        cargo_ids: [...prev.cargo_ids, parseInt(cargoId)]
      }));
      setCargoSelecionado('');
    } else {
      setCargoSelecionado(cargoId);
    }
  };

  const removerCargo = (cargoId) => {
    setFormData(prev => ({
      ...prev,
      cargo_ids: prev.cargo_ids.filter(id => id !== cargoId)
    }));
  };

  const getCargoNome = (cargoId) => {
    const cargo = cargos.find(c => c.id === cargoId);
    return cargo ? cargo.nome : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dadosEnvio = {
        ...formData,
        cargo_ids: formData.cargo_ids,
        irmandade_id: formData.irmandade_id || null
      };
      await api.put(`/colaboradores/${id}`, dadosEnvio);
      navigate('/colaboradores');
    } catch (error) {
      console.error('Erro ao editar colaborador:', error);
      alert('Erro ao editar colaborador. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="cadastro-colaborador-page">
      <div className="page-header">
        <button
          onClick={() => navigate('/colaboradores')}
          className="btn btn-secondary"
        >
          <FiArrowLeft size={20} />
          Voltar
        </button>
        <h1>Editar Colaborador</h1>
      </div>

      <form onSubmit={handleSubmit} className="colaborador-form">
        <div className="form-group">
          <label htmlFor="nome_completo">Nome Completo *</label>
          <input
            type="text"
            id="nome_completo"
            name="nome_completo"
            value={formData.nome_completo}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="tipo_pessoa">Tipo de Pessoa *</label>
            <select
              id="tipo_pessoa"
              name="tipo_pessoa"
              value={formData.tipo_pessoa}
              onChange={handleChange}
              required
            >
              <option value="irmao">Irmão</option>
              <option value="irma">Irmã</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="cargos">Cargos</label>
            <div className="cargo-selection">
              <div className="cargo-input-group">
                <select
                  value={cargoSelecionado}
                  onChange={handleCargoChange}
                  disabled={loadingCargos}
                  className="cargo-select"
                >
                  <option value="">Selecione um cargo</option>
                  {cargos
                    .filter(cargo => !formData.cargo_ids.includes(cargo.id))
                    .map(cargo => (
                      <option key={cargo.id} value={cargo.id}>
                        {cargo.nome}
                      </option>
                    ))
                  }
                </select>
                <button
                  type="button"
                  onClick={adicionarCargo}
                  disabled={!cargoSelecionado || loadingCargos}
                  className="btn btn-add-cargo"
                >
                  <FiPlus size={16} />
                </button>
              </div>
              
              {/* Lista de cargos selecionados */}
              {formData.cargo_ids.length > 0 && (
                <div className="selected-cargos">
                  {formData.cargo_ids.map(cargoId => (
                    <div key={cargoId} className="cargo-tag">
                      <span>{getCargoNome(cargoId)}</span>
                      <button
                        type="button"
                        onClick={() => removerCargo(cargoId)}
                        className="btn-remove-cargo"
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {loadingCargos && <small>Carregando cargos...</small>}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">E-mail</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="telefone">Telefone</label>
            <input
              type="tel"
              id="telefone"
              name="telefone"
              value={formData.telefone}
              onChange={handleChange}
              placeholder="(11) 99999-9999"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="congregacao_id">Congregação</label>
          <select
            id="congregacao_id"
            name="congregacao_id"
            value={formData.congregacao_id}
            onChange={handleChange}
            disabled={loadingCongregacoes}
          >
            <option value="">Selecione uma congregação</option>
            {congregacoes.map(congregacao => (
              <option key={congregacao.id} value={congregacao.id}>
                {congregacao.nome}
              </option>
            ))}
          </select>
          {loadingCongregacoes && <small>Carregando congregações...</small>}
        </div>

        <div className="form-group">
          <label>Vincular a membro (jovem/adulto)</label>
          <MembroBuscaSelect
            membros={membrosJovensAdultos}
            value={formData.irmandade_id}
            loading={loadingIrmandade}
            onChange={(membro) => {
              const nome = membro ? (membro.nome_completo ?? membro.nomeCompleto ?? '') : '';
              setFormData(prev => ({
                ...prev,
                irmandade_id: membro ? String(membro.id) : '',
                ...(membro ? {
                  nome_completo: nome || prev.nome_completo,
                  email: membro.email ?? prev.email,
                  telefone: membro.telefone ?? prev.telefone,
                } : {})
              }));
            }}
          />
          <small className="form-hint">Opcional: vincule este colaborador a um membro da irmandade (jovem ou adulto).</small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/colaboradores')}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
          >
            <FiSave size={20} />
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditarColaborador;